import React, { useState } from 'react';
import {
  formatDispatchDate,
  getDispatchItemBarcode,
  getDispatchItemBrand,
  getDispatchItemCategory,
  getDispatchItemProductName,
  getDispatchItemPrintName,
  getDispatchItemUnit,
  getDispatchItemPackingConfigurations,
  getCalculatedPackingAmounts,
  getPackingConfigUnit,
  getPackingConfigurationsFromNotes,
} from '../utils/dispatchDisplay';

const getCatalogBarcode = (catalogBarcodes, config) =>
  (catalogBarcodes || []).find(
    (barcode) =>
      String(barcode.id || barcode.product_barcode_id) ===
      String(config.product_barcode_id)
  );

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const formatPrice = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toFixed(2);
};

const effectiveDiscountPercent = (mrp, dprice) => {
  const mrpValue = Number(mrp);
  const saleValue = Number(dprice);
  if (!Number.isFinite(mrpValue) || mrpValue <= 0 || !Number.isFinite(saleValue)) return 0;
  return Math.max(0, Math.floor(((mrpValue - saleValue) / mrpValue) * 100));
};

const formatPercent = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return `${numeric.toFixed(2)}%`;
};

const getPriceFromNotes = (notes, label) => {
  const text = String(notes || '');
  const pattern =
    label === 'mrp'
      ? /MRP\s*Rs\.?\s*([0-9]+(?:\.[0-9]+)?)/i
      : label === 'dprice'
        ? /(?:D\.?Price|Discount(?:ed)?(?: Price)?)\s*Rs\.?\s*([0-9]+(?:\.[0-9]+)?)/i
      : /Purchase\s*Rs\.?\s*([0-9]+(?:\.[0-9]+)?)/i;

  return text.match(pattern)?.[1] || '';
};

const hasNoteText = (notes, text) =>
  String(notes || '').toLowerCase().includes(String(text || '').toLowerCase());

const addNoteIfMissing = (notes, text, label) =>
  text && !hasNoteText(notes, label || text) ? text : '';

const getPackageRows = (items, catalogBarcodes = []) =>
  items.flatMap((item) => {
    const configs =
      getDispatchItemPackingConfigurations(item).length > 0
        ? getDispatchItemPackingConfigurations(item)
        : item.product_barcode_id ? [] : getPackingConfigurationsFromNotes(item);
    const noteConfigs = getPackingConfigurationsFromNotes(item);

    if (!configs.length) return [];

    return configs.map((config, index) => {
      const catalogBarcode = getCatalogBarcode(catalogBarcodes, config) || {};
      const noteConfig = noteConfigs[index] || {};
      const mergedConfig = {
        ...noteConfig,
        ...catalogBarcode,
        ...config,
        product_name: catalogBarcode.product_name_eng || catalogBarcode.product_name || config.product_name,
        product_name_eng: catalogBarcode.product_name_eng,
        product_name_tel: catalogBarcode.product_name_tel,
        barcode_quantity:
          config.barcode_quantity ||
          catalogBarcode.quantity ||
          catalogBarcode.barcode_quantity ||
          noteConfig.barcode_quantity,
        unit_short_code:
          config.unit_short_code ||
          catalogBarcode.unit_short_code ||
          catalogBarcode.unit_name ||
          noteConfig.unit_short_code,
        mk_barcode:
          catalogBarcode.mk_barcode ||
          catalogBarcode.mkBarcode ||
          '',
        barcode:
          config.barcode ||
          catalogBarcode.barcode ||
          catalogBarcode.mk_barcode ||
            noteConfig.barcode,
      };
      const calculatedAmounts = getCalculatedPackingAmounts(item, mergedConfig);
      const displayUnitPrice = firstValue(
        calculatedAmounts.packageAmount,
        mergedConfig.final_unit_cost,
        mergedConfig.package_amount,
        mergedConfig.packageAmount,
        mergedConfig.purchase_amount,
        mergedConfig.purchaseAmount,
        getPriceFromNotes(mergedConfig.notes, 'purchase'),
        getPriceFromNotes(item.notes, 'purchase'),
        item.package_amount,
        item.unit_price
      );
      const displayMrp = firstValue(
        mergedConfig.mrp_amount,
        mergedConfig.mrpAmount,
        mergedConfig.MRP,
        mergedConfig.mrp,
        calculatedAmounts.mrpAmount,
        getPriceFromNotes(mergedConfig.notes, 'mrp'),
        getPriceFromNotes(item.notes, 'mrp'),
        item.mrp_amount,
        item.unit_mrp
      );
      const displayDprice = firstValue(
        calculatedAmounts.dprice,
        mergedConfig.dprice,
        mergedConfig.discounted_price,
        mergedConfig.discountedPrice,
        getPriceFromNotes(mergedConfig.notes, 'dprice'),
        getPriceFromNotes(item.notes, 'dprice'),
        displayMrp
      );
      const displayDpriceCalc = firstValue(
        calculatedAmounts.dpriceBeforeRounding,
        mergedConfig.dprice_before_rounding,
        mergedConfig.discounted_price_before_rounding,
        mergedConfig.dpriceBeforeRounding
      );
      const displayDiscountPercent = firstValue(
        mergedConfig.configured_discount_percent,
        mergedConfig.discount_percent,
        mergedConfig.discountPercent,
        displayMrp && displayDprice
          ? effectiveDiscountPercent(displayMrp, displayDprice)
          : ''
      );
      const displayBaseMarginPercent = firstValue(
        mergedConfig.configured_margin_percent,
        mergedConfig.base_margin_percent,
        mergedConfig.margin_percent
      );
      const displayProportionFactor = firstValue(
        mergedConfig.proportion_factor,
        mergedConfig.proportionFactor
      );
      const displayFinalMarginPercent = firstValue(
        mergedConfig.final_margin_percent,
        mergedConfig.finalMarginPercent,
        calculatedAmounts.finalMarginPercent
      );
      const displayMarginAmount = firstValue(
        mergedConfig.margin_amount,
        mergedConfig.marginAmount,
        calculatedAmounts.marginAmount
      );

      return {
      id: `${item.id || getDispatchItemBarcode(item)}-${config.product_barcode_id || index}`,
      printProduct: getDispatchItemPrintName(mergedConfig),
      product:
        mergedConfig.product_name ||
        mergedConfig.product_name_eng ||
        getDispatchItemProductName(item),
      barcode:
        mergedConfig.mk_barcode ||
        mergedConfig.barcode ||
        mergedConfig.bar_code ||
        mergedConfig.product_barcode_id ||
        '-',
      category: getDispatchItemCategory(item),
      brand: getDispatchItemBrand(item),
      qty: mergedConfig.pack_count || mergedConfig.qty || '-',
      unit: getPackingConfigUnit(mergedConfig),
      expDate: item.exp_date,
      unitPrice: displayUnitPrice,
      dprice: displayDprice,
      mrp: displayMrp,
      discountPercent: displayDiscountPercent,
      notes: [
        calculatedAmounts.costCalculationNote || '',
        displayUnitPrice ? `Cost Rs ${displayUnitPrice}` : '',
        mergedConfig.repacking_cost_allocated
          ? `Repacking Rs ${mergedConfig.repacking_cost_allocated}`
          : '',
        mergedConfig.final_unit_cost ? `Final Cost Rs ${mergedConfig.final_unit_cost}` : '',
        Number(displayBaseMarginPercent || 0) > 0
          ? `Base Margin ${Number(displayBaseMarginPercent).toFixed(2)}%`
          : '',
        Number(displayProportionFactor || 0) > 0
          ? `Factor ${Number(displayProportionFactor).toFixed(3)}`
          : '',
        displayFinalMarginPercent
          ? `Final Margin ${Number(displayFinalMarginPercent).toFixed(2)}%`
          : '',
        displayMarginAmount ? `Margin Rs ${displayMarginAmount}` : '',
        Number(displayDiscountPercent || 0) > 0
          ? `Configured Discount ${Number(displayDiscountPercent).toFixed(2)}%`
          : '',
        Number(displayDpriceCalc || 0) > 0
          ? `D.Price Calc Rs ${Number(displayDpriceCalc).toFixed(2)}`
          : '',
        displayMrp && displayDprice
          ? `Effective Discount ${effectiveDiscountPercent(displayMrp, displayDprice)}%`
          : '',
        displayDprice ? `D.Price Rs ${displayDprice}` : '',
        displayMrp ? `MRP Rs ${displayMrp}` : '',
      ]
        .filter(Boolean)
        .join(' | ') || item.notes || '-',
    };
    });
  });

const sourceRows = (items) =>
  items.map((item) => ({
    stockStatus: item.stock_status,
    stockMessage: item.stock_message,
    availableUnits: item.available_units,
    shortageUnits: item.shortage_units,
    pricingAvailable: item.pricing_available,
    id: item.id || `${getDispatchItemBarcode(item)}-${item.exp_date}`,
    product: getDispatchItemProductName(item),
    printProduct: getDispatchItemPrintName(item),
    barcode: getDispatchItemBarcode(item),
    category: getDispatchItemCategory(item),
    brand: getDispatchItemBrand(item),
    qty: item.qty,
    unit: getDispatchItemUnit(item),
    expDate: item.exp_date,
    unitPrice: firstValue(
      item.landed_purchase_unit_price,
      item.landedPurchaseUnitPrice,
      item.unit_price,
      item.package_amount,
      item.packageAmount,
      item.purchase_amount,
      item.purchaseAmount,
      getPriceFromNotes(item.notes, 'purchase'),
      item.inventory_unit_price,
      item.inventoryUnitPrice,
      item.price
    ),
    dprice: firstValue(
      item.dprice,
      item.discounted_price,
      item.discountedPrice,
      getPriceFromNotes(item.notes, 'dprice'),
      item.selling_price,
      item.unit_price
    ),
    dpriceCalc: firstValue(
      item.dprice_before_rounding,
      item.discounted_price_before_rounding,
      item.dpriceBeforeRounding
    ),
    mrp: firstValue(
      item.mrp_amount,
      item.mrpAmount,
      item.MRP,
      item.mrp,
      getPriceFromNotes(item.notes, 'mrp'),
      item.unit_mrp
    ),
    discountPercent: firstValue(
      item.configured_discount_percent,
      item.discount_percent,
      item.discountPercent,
      effectiveDiscountPercent(
        firstValue(
          item.mrp_amount,
          item.mrpAmount,
          item.MRP,
          item.mrp,
          getPriceFromNotes(item.notes, 'mrp'),
          item.unit_mrp
        ),
        firstValue(
          item.dprice,
          item.discounted_price,
          item.discountedPrice,
          getPriceFromNotes(item.notes, 'dprice'),
          item.unit_price
        )
      )
    ),
    notes: [
      item.pricing_notes || item.pricingNotes || item.notes || '',
      addNoteIfMissing(
        item.pricing_notes || item.pricingNotes || item.notes,
        Number(
          firstValue(
            item.dprice_before_rounding,
            item.discounted_price_before_rounding,
            item.dpriceBeforeRounding
          ) || 0
        ) > 0
          ? `D.Price Calc Rs ${Number(
              firstValue(
                item.dprice_before_rounding,
                item.discounted_price_before_rounding,
                item.dpriceBeforeRounding
              )
            ).toFixed(2)}`
          : '',
        'D.Price Calc'
      ),
      firstValue(
        item.mrp_amount,
        item.mrpAmount,
        item.MRP,
        item.mrp,
        getPriceFromNotes(item.notes, 'mrp'),
        item.unit_mrp
      ) && firstValue(
        item.dprice,
        item.discounted_price,
        item.discountedPrice,
        getPriceFromNotes(item.notes, 'dprice'),
        item.unit_price
      )
        ? addNoteIfMissing(
            item.pricing_notes || item.pricingNotes || item.notes,
            `Effective Discount ${effectiveDiscountPercent(
              firstValue(
                item.mrp_amount,
                item.mrpAmount,
                item.MRP,
                item.mrp,
                getPriceFromNotes(item.notes, 'mrp'),
                item.unit_mrp
              ),
              firstValue(
                item.dprice,
                item.discounted_price,
                item.discountedPrice,
                getPriceFromNotes(item.notes, 'dprice'),
                item.unit_price
              )
            )}%`,
            'Effective Discount'
          )
        : '',
    ].filter(Boolean).join(' | ') || '-',
  }));

export const getDispatchTableRows = (items = [], catalogBarcodes = []) =>
  [...items].sort((a, b) => {
    const position = item => Number(/^\[LIST_ORDER:(\d+)\]/.exec(item.notes || '')?.[1] || Infinity);
    return position(a) - position(b);
  }).flatMap(item => {
    const packages = getPackageRows([item], catalogBarcodes);
    const catalog = getCatalogBarcode(catalogBarcodes, item);
    const rows = packages.length ? packages : sourceRows([catalog ? {
      ...item,
      product_name_eng: catalog.product_name_eng || catalog.product_name,
      product_name_tel: catalog.product_name_tel,
      mk_barcode: catalog.mk_barcode,
      barcode: catalog.mk_barcode,
    } : item]);
    return rows;
  });
export const dispatchLineTotal = item => {
  if (item.pricingAvailable === false || item.dprice == null || item.dprice === '') return null;
  const qty = Number(item.qty), price = Number(item.dprice);
  return Number.isFinite(qty) && Number.isFinite(price) ? Math.round(qty * price * 100) / 100 : null;
};
// Bill total is rounded UP to the next whole rupee (e.g. 1211.76 -> 1212); line totals keep paise.
export const dispatchGrandTotal = rows => rows.some(row => dispatchLineTotal(row) === null) ? null : Math.ceil(rows.reduce((sum, row) => sum + Math.round(dispatchLineTotal(row) * 100), 0) / 100);

const DispatchItemsTable = ({
  items = [],
  emptyColSpan = 13,
  catalogBarcodes = [],
}) => {
  const [expandedNotes, setExpandedNotes] = useState({});
  const rows = getDispatchTableRows(items, catalogBarcodes);

  return (
    <>
    <tbody>
      {rows.map((item) => (
        <React.Fragment key={item.id}>
        <tr className="border-t align-top">
          <td className="min-w-[280px] w-[320px] whitespace-normal px-3 py-2 font-semibold">{item.product}
            {item.stockStatus && (
              <div className={'mt-1 text-xs font-medium ' + (item.stockStatus === 'available' ? 'text-emerald-700' : 'text-amber-700')}>
                {item.stockStatus === 'unavailable' ? 'Stock not available' :
                  item.stockStatus === 'insufficient' ? 'Insufficient stock' :
                  item.stockStatus === 'available' ? 'Stock available' : (item.stockMessage || 'Stock availability unknown')}
                {item.availableUnits != null && ' · Available: ' + item.availableUnits}
                {item.shortageUnits > 0 && ' · To purchase: ' + item.shortageUnits}
              </div>
            )}
          </td>

          <td className="px-3 py-2">{item.barcode}</td>

          <td className="px-3 py-2">{item.category}</td>

          <td className="px-3 py-2">{item.brand}</td>

          <td className="px-3 py-2 text-center">{item.qty}</td>

          <td className="px-3 py-2 text-center">{item.unit}</td>

          <td className="px-3 py-2">{formatDispatchDate(item.expDate)}</td>

          <td className="px-3 py-2 text-right">{item.pricingAvailable === false ? '—' : formatPrice(item.unitPrice)}</td>

          <td className="px-3 py-2 text-right">{item.pricingAvailable === false ? '—' : formatPrice(item.mrp)}</td>
          <td className="px-3 py-2 text-right">{item.pricingAvailable === false ? '—' : formatPercent(item.discountPercent)}</td>
          <td className="px-3 py-2 text-right">{item.pricingAvailable === false ? '—' : formatPrice(item.dprice)}</td>
          <td className="px-3 py-2 text-right">{formatPrice(dispatchLineTotal(item))}</td>

          <td className="px-3 py-2 whitespace-nowrap">
            {item.notes && item.notes !== '-' ? (
              <button
                type="button"
                aria-expanded={Boolean(expandedNotes[item.id])}
                onClick={() => setExpandedNotes((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                className="text-emerald-700 underline"
              >
                {expandedNotes[item.id] ? 'Hide notes' : 'Show notes'}
              </button>
            ) : '—'}
          </td>
        </tr>
        {expandedNotes[item.id] && (
          <tr className="border-t bg-emerald-50">
            <td colSpan={emptyColSpan} className="px-4 py-3">
              <div className="mb-1 text-xs font-semibold text-emerald-800">Notes — {item.product}</div>
              <div className="whitespace-pre-wrap break-words text-sm text-gray-700">{item.notes}</div>
            </td>
          </tr>
        )}
        </React.Fragment>
      ))}

      {rows.length === 0 && (
        <tr>
          <td colSpan={emptyColSpan} className="px-3 py-5 text-center text-gray-500">
            No items
          </td>
        </tr>
      )}
    </tbody>
    <tfoot className="border-t bg-slate-100 font-semibold"><tr><td colSpan={11} className="px-3 py-3 text-right">Grand total</td><td className="px-3 py-3 text-right">{formatPrice(dispatchGrandTotal(rows))}</td><td /></tr></tfoot>
    </>
  );
};

export default DispatchItemsTable;
